/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import buildSearchQuery from "./build-search-query";
import * as actions from "./actions";
import { NOTIFICATION_SUCCESS, NOTIFICATION_ERROR } from "containers/page/actions";
import { getWorkspaceData, deleteStory, saveFilters, updateSavedFilter } from "helpers/api";
import { modifySavedFilter, modifyForApi } from "./filter-conversion";
import { navigateFn } from "../../utils/routes.utils";
import { WORKSPACE_TABS_PATH } from "./routes";
import pDebounce from "p-debounce";
import { t } from "i18n";

const debouncedGetData: any = pDebounce(getWorkspaceData, 250);

export const setSavedFilterAction = (filter: any, modifiedFilter: any) => (dispatch: any) => {
  dispatch({ type: actions.WORKSPACE_SET_SELECTED_SAVED_FILTER, payload: { filter } });

  const filterSet = modifiedFilter.filter;
  Object.keys(filterSet).forEach((key) => {
    let value = filterSet[key];
    dispatch({ type: actions.WORKSPACE_UPDATE_STAGING_FILTER, payload: { key, value } });
  });
};

export const fetchWorkspaceData: any = (selectedTabSlug: any, defaultFilter: any) => (dispatch: any, getState: any) => {
  const modifiedDefaultFilter = defaultFilter ? modifySavedFilter(defaultFilter) : defaultFilter;

  if (modifiedDefaultFilter) {
    dispatch(setSavedFilterAction(defaultFilter, modifiedDefaultFilter));
    dispatch({ type: actions.WORKSPACE_REPLACE_FILTER, payload: { filterSet: modifiedDefaultFilter.filter } });
  }

  const filterSet = (modifiedDefaultFilter && modifiedDefaultFilter.filter) || getState().workspace.currentFilterSet;
  dispatch({ type: actions.WORKSPACE_FETCH_DATA, payload: { filterSet } });
  const query = buildSearchQuery({ ...filterSet, selectedTabSlug });
  getWorkspaceData(query).then(
    (data) => dispatch({ type: actions.WORKSPACE_DATA_SUCCESS, payload: { data } }),
    () => {
      dispatch({ type: actions.WORKSPACE_ADD_ERRORS, payload: "fetchingStories" });
    }
  );
};

export const fetchWorkspaceSearchData = (selectedTabSlug: any, defaultFilter: any) => (
  dispatch: any,
  getState: any
) => {
  const modifiedDefaultFilter = defaultFilter ? modifySavedFilter(defaultFilter) : defaultFilter;

  if (modifiedDefaultFilter) {
    dispatch(setSavedFilterAction(defaultFilter, modifiedDefaultFilter));
    dispatch({ type: actions.WORKSPACE_REPLACE_FILTER, payload: { filterSet: modifiedDefaultFilter.filter } });
  }

  const filterSet = (modifiedDefaultFilter && modifiedDefaultFilter.filter) || getState().workspace.currentFilterSet;
  dispatch({ type: actions.WORKSPACE_FETCH_DATA, payload: { filterSet } });
  const query = buildSearchQuery({ ...filterSet, selectedTabSlug });
  debouncedGetData(query).then(
    (data: any) => dispatch({ type: actions.WORKSPACE_DATA_SUCCESS, payload: { data } }),
    () => {
      dispatch({ type: actions.WORKSPACE_ADD_ERRORS, payload: "fetchingStories" });
    }
  );
};

export const deleteWorkspaceStory = (storyId: any, selectedTabSlug: any, onClose: any) => (dispatch: any) => {
  dispatch({ type: actions.WORKSPACE_DELETE_STORY });
  deleteStory(storyId)
    .then(() => onClose(selectedTabSlug))
    .then(() => dispatch(fetchWorkspaceData(selectedTabSlug)));
};

export const saveFiltersAction = (filterSet: any, filterName: any, tabSlug: any) => (dispatch: any) => {
  const navigate = navigateFn(dispatch);
  const filterForApi = modifyForApi({ filter: filterSet });

  dispatch({ type: actions.WORKSPACE_FILTER_SAVE });
  saveFilters(filterForApi.filter, filterName).then(
    (filter) => {
      dispatch({ type: actions.WORKSPACE_SET_SELECTED_SAVED_FILTER, payload: { filter } });
      navigate(WORKSPACE_TABS_PATH, { tabSlug });
      dispatch({
        type: NOTIFICATION_SUCCESS,
        payload: { message: t("workspace.filter-save-success") }
      });
    },
    () => {
      dispatch({ type: actions.WORKSPACE_REMOVE_LOADER });
      dispatch({
        type: NOTIFICATION_ERROR,
        payload: {
          message: t("workspace.filter-save-failed"),
          action: {
            label: "Retry",
            callback: () => dispatch(saveFiltersAction(filterSet, filterName, tabSlug))
          }
        }
      });
    }
  );
};

export const updateFilterAction = (savedFilter: any, filterSet: any) => (dispatch: any) => {
  const filter = { ...savedFilter, filter: filterSet };
  const filterForApi = modifyForApi(filter);

  dispatch({ type: actions.WORKSPACE_UPDATE_SELECTED_SAVED_FILTER });
  updateSavedFilter(savedFilter.id, filterForApi);

  dispatch({ type: actions.WORKSPACE_SET_SELECTED_SAVED_FILTER, payload: { filter: filterForApi } });
};

export const updatePageReferer = (referer: any) => (dispatch: any) => {
  dispatch({ type: actions.WORKSPACE_UPDATE_PAGE_REFERER, payload: { referer } });
};
